import { useCallback, useEffect, useRef, useState } from "react";

const isIOSSafari =
  /iPhone|iPad/.test(navigator.userAgent) &&
  /Safari/.test(navigator.userAgent) &&
  !/CriOS|FxiOS/.test(navigator.userAgent);

const SpeechRecognitionAPI = (
  (window as unknown as Record<string, unknown>).SpeechRecognition ||
  (window as unknown as Record<string, unknown>).webkitSpeechRecognition
) as { new (): SpeechRecognition } | undefined;

const ERROR_MESSAGES: Record<string, string> = {
  "not-allowed": "Microphone access denied",
  "no-speech": "No speech detected",
  "audio-capture": "Microphone not found",
  network: "Connection to speech service failed. Try again?",
  aborted: "",
  "service-not-allowed": "Voice input not available in this browser",
};

export function useSpeechRecognition(
  onResult: (text: string) => void,
) {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [interimText, setInterimText] = useState("");
  const [error, setError] = useState("");
  const [isAvailable, setIsAvailable] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    if (isIOSSafari || !SpeechRecognitionAPI) return;

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = navigator.language || "en-US";

    recognition.onstart = () => {
      setIsListening(true);
      setIsProcessing(false);
      setError("");
      setInterimText("");
    };

    recognition.onresult = (event: any) => {
      let interim = "";
      let final = "";
      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }
      if (interim) setInterimText(interim);
      if (final) {
        onResultRef.current(final);
        setInterimText("");
        setIsProcessing(false);
        setIsListening(false);
      }
    };

    recognition.onerror = (event: any) => {
      setIsListening(false);
      setIsProcessing(false);
      setInterimText("");
      const msg = ERROR_MESSAGES[event.error] || `Recognition failed: ${event.error}`;
      if (msg) {
        setError(msg);
        errorTimerRef.current = setTimeout(() => setError(""), event.error === "network" ? 4000 : 3000);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimText("");
    };

    recognitionRef.current = recognition;
    setIsAvailable(true);

    return () => {
      recognition.abort();
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      setIsAvailable(false);
    };
  }, []);

  const toggle = useCallback(async () => {
    if (!recognitionRef.current || isProcessing) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }

    try {
      const permissionStatus = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      if (permissionStatus.state === "denied") {
        setError("Microphone access denied");
        errorTimerRef.current = setTimeout(() => setError(""), 3000);
        return;
      }
    } catch {
      // permissions API not available — proceed anyway
    }

    try {
      recognitionRef.current.start();
      setIsProcessing(true);
    } catch {
      setIsListening(false);
      setIsProcessing(false);
      setError("Failed to start voice input");
      errorTimerRef.current = setTimeout(() => setError(""), 3000);
    }
  }, [isListening, isProcessing]);

  return { isListening, isProcessing, interimText, error, isAvailable, toggle };
}
